import { Capacitor } from "@capacitor/core";
import { LocalNotifications } from "@capacitor/local-notifications";
import { dueReminders, staleReminderIds, type MealReminder } from "./mealReminders";
import type { PlanDay } from "./types";

// The native half of meal reminders: permission, the notification's buttons,
// and keeping what's pending in step with the plan. Every call is a no-op off
// iOS, so callers don't have to ask where they're running.

/** The action set attached to every meal reminder. Registered once per launch;
 *  iOS matches it by id when the notification is delivered. */
export const MEAL_ACTION_TYPE = "AFFINAGE_MEAL";
export const ACTION_EATEN = "meal-eaten";

let registered = false;

export function remindersSupported(): boolean {
  return Capacitor.isNativePlatform();
}

/** Register the "Ate it" button. Safe to call repeatedly. */
export async function registerMealActions(): Promise<void> {
  if (!remindersSupported() || registered) return;
  try {
    await LocalNotifications.registerActionTypes({
      types: [
        {
          id: MEAL_ACTION_TYPE,
          actions: [
            // No destructive counterpart: "I didn't eat it" is the default
            // state, so swiping the notification away already says it. A
            // second button would only add a decision.
            { id: ACTION_EATEN, title: "Ate it" },
          ],
        },
      ],
    });
    registered = true;
  } catch {
    // Older binary without the plugin — reminders simply won't schedule.
  }
}

export async function requestReminderPermission(): Promise<boolean> {
  if (!remindersSupported()) return false;
  try {
    const { display } = await LocalNotifications.requestPermissions();
    return display === "granted";
  } catch {
    return false;
  }
}

export async function hasReminderPermission(): Promise<boolean> {
  if (!remindersSupported()) return false;
  try {
    const { display } = await LocalNotifications.checkPermissions();
    return display === "granted";
  } catch {
    return false;
  }
}

/**
 * Make what's pending match the plan.
 *
 * Cancel-then-schedule rather than diff: a meal's time never changes but its
 * existence and its ticked state do, and "cancel everything of ours, schedule
 * what's due" has no state to drift. Same reasoning as the Health push.
 */
export async function syncMealReminders(days: PlanDay[], enabled: boolean): Promise<number> {
  if (!remindersSupported()) return 0;
  try {
    const due = enabled ? dueReminders(days) : [];
    const pending = await LocalNotifications.getPending();
    const stale = staleReminderIds(
      pending.notifications.map((n) => n.id),
      due
    );
    if (stale.length > 0) {
      await LocalNotifications.cancel({ notifications: stale.map((id) => ({ id })) });
    }
    if (due.length === 0) return 0;

    await registerMealActions();
    await LocalNotifications.schedule({
      notifications: due.map((r: MealReminder) => ({
        id: r.id,
        title: r.title,
        body: r.body,
        schedule: { at: r.at },
        actionTypeId: MEAL_ACTION_TYPE,
        sound: "default",
        // The day and slot the answer belongs to. Read back when the action
        // fires — the notification may be answered long after the app that
        // scheduled it was last open.
        extra: { date: r.date, slot: r.slot },
      })),
    });
    return due.length;
  } catch {
    // A binary without the plugin, or permission withdrawn between check and
    // schedule. Nothing to recover — reminders are an assist, not a promise.
    return 0;
  }
}

/** Take every meal reminder down — used when the toggle goes off. */
export async function clearMealReminders(): Promise<void> {
  if (!remindersSupported()) return;
  try {
    const pending = await LocalNotifications.getPending();
    const ours = pending.notifications.map((n) => n.id).filter((id) => id >= 40_000);
    if (ours.length > 0) {
      await LocalNotifications.cancel({ notifications: ours.map((id) => ({ id })) });
    }
  } catch {
    // ignore
  }
}
